// ─── Live → Iridium Messages ─────────────────────────────────────────────────
// Read-only, paginated list of a logger's Iridium satellite messages. Shown only
// for Iridium loggers (device.iridium) — DashboardPage appends the tab and gates
// its render on that flag, so this component assumes the device is Iridium.
//
// Data comes from /api/project/<pid>/device/<did>/iridium-messages/ (the
// project-scoped mirror of the admin Iridium list): messages are matched by the
// device FK, falling back to IMEI, multi-chunk messages (type 254) are grouped
// into one row, and message_contents is an uppercase hex string.

import { useEffect, useState, useCallback } from 'react'
import { Loader, AlertCircle, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { fetchIridiumMessages } from '../../lib/projectsApi'
import LiveIconTabs from './LiveIconTabs'

const PER_PAGE = 25

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border-gray-200 rounded-lg shadow-sm px-3 py-4 ${className}`}>
    {children}
  </div>
)

const fmtTime = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d) ? '—' : d.toLocaleString()
}

export default function IridiumMessagesTab({
  projectId, deviceId, currentLiveIndex, setCurrentLiveIndex,
  devices = [], onSelectDevice, showHeader = true, showIridium = false,
}) {
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Reset to the first page whenever the device or the active search changes.
  useEffect(() => { setPage(1) }, [deviceId, query])

  const load = useCallback(async () => {
    if (!projectId || !deviceId) { setRows([]); return }
    setLoading(true)
    setError(null)
    try {
      const res = await fetchIridiumMessages(projectId, deviceId, { page, perPage: PER_PAGE, search: query })
      if (res && res.success) {
        setRows(res.data || [])
        setTotalPages(res.total_pages || 1)
        setTotalCount(res.total_count || 0)
      } else {
        setError((res && res.error) || 'Failed to load Iridium messages.')
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load Iridium messages.')
    } finally {
      setLoading(false)
    }
  }, [projectId, deviceId, page, query])

  useEffect(() => { load() }, [load])

  const onSearchSubmit = (e) => { e.preventDefault(); setQuery(search.trim()) }

  const header = showHeader ? (
    <Card className="!py-2 !border-none">
      <LiveIconTabs
        currentLiveIndex={currentLiveIndex}
        setCurrentLiveIndex={setCurrentLiveIndex}
        devices={devices}
        deviceId={deviceId}
        onSelectDevice={onSelectDevice}
        showIridium={showIridium}
      />
    </Card>
  ) : null

  if (!deviceId) {
    return (
      <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
        {header}
        <div className="p-6 text-sm text-gray-500">Select a device to view its Iridium messages.</div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
      {header}
      <Card>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="text-sm font-semibold text-[#4D4D4D]">
            Iridium Messages
            {totalCount > 0 && <span className="ml-2 text-xs font-normal text-gray-400">({totalCount})</span>}
          </h3>
          <form onSubmit={onSearchSubmit} className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search IMEI, type, chunk…"
                className="pl-8 pr-3 py-1.5 border border-[#D6D6D6] rounded-md text-xs text-gray-700 bg-white w-56 focus:outline-none focus:border-[#358ECD]"
              />
            </div>
            <button type="submit" className="px-3 py-1.5 text-xs font-semibold text-white bg-[#358ECD] rounded-md hover:bg-[#2e7cb5]">
              Search
            </button>
          </form>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 py-4">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left border-collapse">
            <thead>
              <tr className="text-[#4D4D4D] border-b border-gray-200">
                <th className="py-2 pr-3 font-semibold whitespace-nowrap">Time Sent</th>
                <th className="py-2 pr-3 font-semibold whitespace-nowrap">IMEI</th>
                <th className="py-2 pr-3 font-semibold whitespace-nowrap">Type</th>
                <th className="py-2 pr-3 font-semibold whitespace-nowrap">Chunks</th>
                <th className="py-2 pr-3 font-semibold">Message (hex)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-6">
                  <div className="flex items-center gap-2 text-gray-500"><Loader className="w-4 h-4 animate-spin" /> Loading…</div>
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="py-6 text-gray-500">No Iridium messages found.</td></tr>
              ) : rows.map((m) => (
                <tr key={m.id} className="border-b border-gray-100 align-top">
                  <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{fmtTime(m.time_sent)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{m.imei || '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{m.message_type ?? '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-gray-700">
                    {m.is_multi_chunk ? `1–${m.total_chunks}` : `${m.chunk_number}/${m.total_chunks}`}
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-gray-600 break-all">{m.message_contents || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-3 text-xs text-gray-600">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:border-[#358ECD]"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:border-[#358ECD]"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </Card>
    </div>
  )
}
